import type { Identifier, RaRecord } from "ra-core";
import {
  CreateBase,
  Form,
  useGetIdentity,
  useListContext,
  useNotify,
  useRecordContext,
  useResourceContext,
  useUpdate,
  useTranslate,
} from "ra-core";
import { useFormContext } from "react-hook-form";

import { SaveButton } from "@/components/admin";
import { NoteInputs } from "./NoteInputs";
import { getCurrentDate } from "./utils";

const foreignKeyMapping = {
  contacts: "contact_id",
  deals: "deal_id",
};

export const NoteCreate = ({
  reference,
  showStatus,
}: {
  reference: "contacts" | "deals";
  showStatus?: boolean;
}) => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const { identity } = useGetIdentity();

  if (!record || !identity) return null;

  return (
    <CreateBase resource={resource} redirect={false}>
      <Form>
        <div className="space-y-3">
          <NoteInputs showStatus={showStatus} />
          <NoteCreateButton reference={reference} record={record} />
        </div>
      </Form>
    </CreateBase>
  );
};

const NoteCreateButton = ({
  reference,
  record,
}: {
  reference: "contacts" | "deals";
  record: RaRecord<Identifier>;
}) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { reset } = useFormContext();
  const { refetch } = useListContext();
  const translate = useTranslate();

  if (!record || !identity) return null;

  const resetValues: {
    date: string;
    text: null;
    attachments: null;
    status?: string;
  } = {
    date: getCurrentDate(),
    text: null,
    attachments: null,
  };

  if (reference === "contacts") {
    resetValues.status = "warm";
  }

  const handleSuccess = (data: any) => {
    reset(resetValues, { keepValues: false });
    refetch();
    update(reference, {
      id: (record && record.id) as unknown as Identifier,
      data: { last_seen: new Date().toISOString(), status: data.status },
      previousData: record,
    });
    notify(translate("simplecrm.notes.note_added", { _: "Nota adicionada" }));
  };

  return (
    <div className="flex justify-end">
      <SaveButton
        type="button"
        label={translate("simplecrm.notes.add_this_note", { _: "Adicionar esta nota" })}
        transform={(data) => ({
          ...data,
          [foreignKeyMapping[reference]]: record.id,
          sales_id: identity.id,
          date: data.date || getCurrentDate(),
        })}
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      >
        {translate("simplecrm.notes.add_this_note", { _: "Adicionar esta nota" })}
      </SaveButton>
    </div>
  );
};
